import _, {chain, isEqual, partition, without} from 'lodash';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {isWithinBounds, makeMessageBoundaryBuilder} from '../../../../helpers/cellsHelpers';
import {
  findDraftCommentForLastMessage,
  getDraftMessagesAndConversationLastMessages,
} from '../../../../helpers/models/messageHelpers';
import {collapseById} from '../../../../models/api/dataGroupModel';
import {AppAction} from '../../../appActions';
import {AsyncMoreStatusesEnum} from '../../../appTypes';
import {DataCascade} from '../../../data/dataCascade';
import {DataGroupsActionsEnum} from '../../../data/dataGroupsActions';
import {MessageActionsEnum} from '../../../data/messages/messagesActions';
import {InboxesParametersTypesEnum} from '../../inboxes/parameters/inboxesParameters';
import {
  CellsPanelMessagesActionsEnum,
  CellsPanelPageActionsEnum,
  isCellsPanelPageAction,
} from '../cellsPanelActions';
import {CellsPanelMessagesPageState} from '../cellsPanelState';
import {CellsContext} from '../context/cellsContext';
import {isMessageInPanel} from '../helpers/cellsPanelHelpers';
import {computeMoreStatusForBoundary, updateMoreStatus} from '../helpers/cellsPanelReducerHelpers';

export function cellsPanelMessagesReducer(
  state: CellsPanelMessagesPageState,
  action: AppAction,
  dataCascade: DataCascade,
): CellsPanelMessagesPageState {
  // If this is a cells page action, check that the uid matches.
  if (isCellsPanelPageAction(action) && action.payload.pageUid !== state.uid) {
    return state;
  }

  // Otherwise, proceed as usual.
  switch (action.type) {
    case CellsPanelPageActionsEnum.PAGE_NAVIGATED: {
      const {parameters} = action.payload;
      if (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
        throw new Error('Incorrect parameters type.');
      }

      return {
        ...state,
        cellsContext: buildCellsContext(state.cellsContext, action.payload.cellsContext),
        parameters,
      };
    }

    case CellsPanelPageActionsEnum.REFETCH_STARTED:
      // If we are refetching a previously loaded page, only set the refetching flag for the mobile app.
      if (state.status === AsyncStatusesEnum.LOADED) {
        return {
          ...state,
          isRefetching: true,
        };
      }

      return {
        ...state,
        status: AsyncStatusesEnum.LOADING,
      };

    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED: {
      const {topBoundary, bottomBoundary, ids, isLoadedFromCache} = action.payload;

      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        isLoadedFromCache,
        isRefetching: false,
        moreStatusTop: computeMoreStatusForBoundary(topBoundary),
        moreStatusBottom: computeMoreStatusForBoundary(bottomBoundary),
        topBoundary,
        bottomBoundary,
        messageUids: ids,
      };
    }

    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_FAILED:
      if (state.status === AsyncStatusesEnum.LOADED && state.isLoadedFromCache) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
        messageUids: [],
      };

    case CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES:
      return {
        ...state,
        ...updateMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.LOADING),
      };

    case CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED: {
      const {payload} = action;
      const messageUids = _(state.messageUids).concat(payload.ids).uniq().value();

      if (payload.direction === ScrollDirectionsEnum.TOP) {
        return {
          ...state,
          moreStatusTop: computeMoreStatusForBoundary(payload.topBoundary),
          topBoundary: payload.topBoundary,
          messageUids,
        };
      }

      return {
        ...state,
        moreStatusBottom: computeMoreStatusForBoundary(payload.bottomBoundary),
        bottomBoundary: payload.bottomBoundary,
        messageUids,
      };
    }

    case CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_FAILED:
      return {
        ...state,
        ...updateMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.ERROR),
      };

    case MessageActionsEnum.MESSAGE_DELETED: {
      // Message not currently visible: ignore.
      const {messageUids} = state;
      if (!messageUids.includes(action.payload.messageUid)) {
        return state;
      }

      return {
        ...state,
        messageUids: without(messageUids, action.payload.messageUid),
      };
    }

    default: {
      if (action.type === DataGroupsActionsEnum.DATA_UPDATED && action.payload.shouldNotCascade) {
        return state;
      }

      const {parameters, messageUids} = state;

      const {messages: updatedMessages, conversations} = dataCascade.updatedResources;

      // In order to support "trashing" comment draft conversations, we need to handle when the conversation has been updated.
      const messages = getDraftMessagesAndConversationLastMessages(
        parameters,
        conversations ?? [],
        updatedMessages ?? [],
        dataCascade.data.messages,
      );

      if (!messages || messages.length === 0) {
        return state;
      }

      // Find which of the updated messages should appear or not in the list.
      const boundaryBuilder = makeMessageBoundaryBuilder(parameters);
      const [shouldAppear, shouldNotAppear] = partition(collapseById(messages), (m) => {
        const draftComment = findDraftCommentForLastMessage(
          parameters,
          dataCascade.data.userId,
          m,
          dataCascade.data.conversations.byId,
          dataCascade.data.comments.byId,
        );

        if (!isMessageInPanel(dataCascade.data, parameters, m, draftComment)) {
          return false;
        }

        const boundary = boundaryBuilder(m, draftComment);
        return isWithinBounds(state, parameters.sortDirection, boundary);
      });

      // Find what to update.
      const shouldBeAdded = chain(shouldAppear)
        .map((m) => m.uid)
        .difference(messageUids)
        .value();
      const shouldBeRemoved = chain(shouldNotAppear)
        .map((m) => m.uid)
        .intersection(messageUids)
        .value();

      // We may not need to do anything.
      if (shouldBeAdded.length === 0 && shouldBeRemoved.length === 0) {
        return state;
      }

      // Otherwise, find the new conversation IDs.
      const newMessageUids = _(messageUids).concat(shouldBeAdded).difference(shouldBeRemoved).value();

      // Update the state with the new list.
      return {
        ...state,
        messageUids: newMessageUids,
      };
    }
  }
}

/*
 * Helpers.
 */

function buildCellsContext(existingCellsContext: CellsContext, updatedCellsContext: CellsContext) {
  if (isEqual(updatedCellsContext, existingCellsContext)) {
    // Prevent an identity change.
    return existingCellsContext;
  }

  return updatedCellsContext;
}
